import { useState } from "react";
import { Navigate } from "react-router-dom";
import { LoaderCircle, TriangleAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark, useBrand } from "@/context/BrandContext";

const BG =
  "https://images.unsplash.com/photo-1534523456099-480ef6736f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW9vZHklMjBzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwbmlnaHR8ZW58MHx8fHwxNzg2Njg4OTMwfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const brand = useBrand();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(username.trim().toUpperCase(), password);
    setBusy(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#0A0A0A]">
      <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-black/75" />
      <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full border border-[#00F0FF]/15">
        <div className="radar-sweep h-full w-full origin-center">
          <div className="absolute left-1/2 top-0 h-1/2 w-px bg-gradient-to-b from-[#00F0FF] to-transparent" />
        </div>
      </div>

      <div className="relative z-10 flex min-h-screen flex-col justify-between px-6 py-10 sm:px-16 lg:px-24">
        <div className="flex items-center gap-3">
          <BrandMark brand={brand} size={24} showTagline />
        </div>

        <div className="max-w-md rise">
          <p className="overline mb-4">secure terminal access</p>
          <h1 className="font-head text-4xl font-black leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
            EVERY TRUCK.
            <br />
            <span className="text-[#00F0FF]">LIVE.</span>
          </h1>
          <p className="mt-5 max-w-sm text-sm text-[#A1A1AA]">
            Real-time positions, ignition state, road distance and ETA for the entire fleet.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="overline block mb-2">operator id</label>
              <input
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                placeholder="SHANKAR"
                className="w-full border border-white/15 bg-[#121212]/80 px-4 py-3 text-sm uppercase tracking-[0.15em] text-white outline-none placeholder:text-[#3f3f46] focus:border-[#00F0FF]"
              />
            </div>
            <div>
              <label className="overline block mb-2">passcode</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full border border-white/15 bg-[#121212]/80 px-4 py-3 text-sm tracking-[0.15em] text-white outline-none placeholder:text-[#3f3f46] focus:border-[#00F0FF]"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="flex items-center gap-2 border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2 text-xs text-[#FF3B30]"
              >
                <TriangleAlert size={14} /> {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 border border-[#00F0FF] bg-[#00F0FF] px-4 py-3 text-sm font-bold uppercase tracking-[0.2em] text-black transition-colors duration-150 hover:bg-transparent hover:text-[#00F0FF] disabled:opacity-50"
            >
              {busy ? <LoaderCircle size={16} className="animate-spin" /> : null}
              {busy ? "authenticating" : "engage"}
            </button>
          </form>
        </div>

        <p className="overline">
          {brand.brand_name} © 2026 - authorised dispatch personnel only
          {brand.brand_website ? ` · ${brand.brand_website}` : ""}
        </p>
      </div>
    </div>
  );
}
